import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { History, RefreshCw } from 'lucide-react';
import PartnerLayout from '../../components/PartnerLayout';
import { partnerPortalAPI } from '../../lib/partnerApi';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return (
    <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold capitalize', colors[status] || 'bg-muted text-gray-600')}>
      {status}
    </span>
  );
};

const PartnerWithdrawalHistoryPage = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnerPortalAPI.withdrawals({ status: 'all' });
      setWithdrawals(res.data.withdrawals || []);
    } catch { toast.error('Failed to load withdrawal history'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalCompleted = withdrawals.filter(w => w.status === 'completed').reduce((s, w) => s + w.amount, 0);
  const totalPending = withdrawals.filter(w => w.status === 'pending').reduce((s, w) => s + w.amount, 0);

  return (
    <PartnerLayout>
      <div data-testid="partner-withdrawal-history" className="space-y-6 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Withdrawal History</h1>
            <p className="text-sm text-muted-foreground mt-0.5">All your withdrawal requests and their statuses</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card rounded-xl border border-border p-4 text-center shadow-sm">
            <p className="text-2xl font-bold">{withdrawals.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Requests</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-green-600">${totalCompleted.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Paid Out</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-yellow-600">${totalPending.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Pending</p>
          </div>
        </div>

        {/* Table */}
        <div data-testid="withdrawal-history-table" className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  {['Date', 'Amount', 'Method', 'Status', 'Processed On', 'Notes'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                ) : withdrawals.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No withdrawal history yet</td></tr>
                ) : (
                  withdrawals.map((w, i) => (
                    <tr key={w._id || i} className="border-t border-border hover:bg-muted">
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDate(w.requested_at)}</td>
                      <td className="px-4 py-3 font-semibold text-sm">${w.amount.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{(w.payment_method || '—').replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3"><StatusBadge status={w.status} /></td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(w.processed_at)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{w.payment_details?.notes || w.rejection_reason || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerWithdrawalHistoryPage;
